import React, { useState, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, TrendingUp, FileText, Package, FlaskConical, Zap, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Scale } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { formatArgentinaDate } from '../../utils/dateUtils';

interface SalesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  salesData: {
    totalSales: number;
    invoiceSales: number;
    productionSales: number;
    sampleSales: number;
    expressSales: number;
    expressSampleSales: number;
  };
  period: string;
  periodInvoices: any[];
  periodProductions: any[];
  periodSamples: any[];
  periodExpress: any[];
  periodExpressSamples: any[];
  clients: any[];
}

const SalesDetailModal: React.FC<SalesDetailModalProps> = ({
  isOpen,
  onClose,
  salesData,
  period,
  periodInvoices,
  periodProductions,
  periodSamples,
  periodExpress,
  periodExpressSamples,
  clients
}) => {
  const [expandedSections, setExpandedSections] = useState<{
    invoices: boolean;
    productions: boolean;
    samples: boolean;
    express: boolean;
    expressSamples: boolean;
  }>({
    invoices: true,
    productions: false,
    samples: false,
    express: false,
    expressSamples: false
  });

  // Total facturado = solo facturas
  const totalFacturado = salesData.invoiceSales;

  // Total pedidos cargados = producciones + muestras + express + express samples
  const totalPedidosCargados = useMemo(() => {
    return salesData.productionSales + salesData.sampleSales + salesData.expressSales + salesData.expressSampleSales;
  }, [salesData]);

  // Diferencia (facturado - pedidos)
  const diferencia = totalFacturado - totalPedidosCargados;
  const balanceado = Math.abs(diferencia) < 1000; // Tolerancia de $1000

  // Facturas agrupadas por tipo de venta
  const invoicesBySaleType = useMemo(() => {
    const grouped = {
      production: periodInvoices.filter(inv => inv.sale_type === 'production'),
      sample: periodInvoices.filter(inv => inv.sale_type === 'sample'),
      express: periodInvoices.filter(inv => inv.sale_type === 'express'),
      express_sample: periodInvoices.filter(inv => inv.sale_type === 'express_sample'),
      other: periodInvoices.filter(inv => inv.sale_type === 'other'),
      sin_tipo: periodInvoices.filter(inv => !inv.sale_type),
    };
    return grouped;
  }, [periodInvoices]);

  // Totales por tipo de venta
  const totalsBySaleType = useMemo(() => {
    const calc = (invs: any[]) => invs.reduce((sum, inv) => {
      const isUSD = inv.currency === 'USD';
      const amount = inv.subtotal || inv.total || 0;
      return sum + (isUSD && inv.exchange_rate ? amount * inv.exchange_rate : amount);
    }, 0);
    return {
      production: calc(invoicesBySaleType.production),
      sample: calc(invoicesBySaleType.sample),
      express: calc(invoicesBySaleType.express),
      express_sample: calc(invoicesBySaleType.express_sample),
      other: calc(invoicesBySaleType.other),
      sin_tipo: calc(invoicesBySaleType.sin_tipo),
    };
  }, [invoicesBySaleType]);

  // Format period for display
  const getPeriodDisplay = (period: string) => {
    switch (period) {
      case 'month': return 'este mes';
      case 'month-1': return 'mes anterior';
      case 'month-2': return 'hace 2 meses';
      case 'month-3': return 'hace 3 meses';
      case 'month-4': return 'hace 4 meses';
      case 'quarter': return 'este trimestre';
      case 'year': return 'este año';
      case 'custom': return 'período seleccionado';
      default: return period || 'el período';
    }
  };

  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get client name by ID
  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Cliente desconocido';
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[100] max-h-[90vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-4 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            Control de Facturación ({getPeriodDisplay(period)})
          </Dialog.Title>
          
          <div className="space-y-5">
            {/* Resumen comparativo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Total Facturado */}
              <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg">
                <div className="flex items-center mb-1">
                  <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                  <p className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Total Facturado</p>
                </div>
                <p className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">{formatCurrency(totalFacturado)}</p>
                <p className="text-xs text-indigo-600 dark:text-indigo-400">{periodInvoices.length} facturas</p>
              </div>

              {/* Total Pedidos Cargados */}
              <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg">
                <div className="flex items-center mb-1">
                  <Package className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
                  <p className="text-xs font-medium text-purple-700 dark:text-purple-300">Pedidos Cargados</p>
                </div>
                <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">{formatCurrency(totalPedidosCargados)}</p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {periodProductions.length + periodSamples.length + periodExpress.length + periodExpressSamples.length} pedidos
                </p>
              </div>

              {/* Diferencia / Balance */}
              <div className={`p-4 rounded-lg ${
                balanceado 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30' 
                  : diferencia > 0 
                    ? 'bg-amber-50 dark:bg-amber-900/30'
                    : 'bg-red-50 dark:bg-red-900/30'
              }`}>
                <div className="flex items-center mb-1">
                  <Scale className={`h-4 w-4 mr-2 ${
                    balanceado 
                      ? 'text-emerald-600 dark:text-emerald-400' 
                      : diferencia > 0 
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-red-600 dark:text-red-400'
                  }`} />
                  <p className={`text-xs font-medium ${
                    balanceado 
                      ? 'text-emerald-700 dark:text-emerald-300' 
                      : diferencia > 0 
                        ? 'text-amber-700 dark:text-amber-300'
                        : 'text-red-700 dark:text-red-300'
                  }`}>Diferencia</p>
                </div>
                <p className={`text-2xl font-bold ${
                  balanceado 
                    ? 'text-emerald-800 dark:text-emerald-200' 
                    : diferencia > 0 
                      ? 'text-amber-800 dark:text-amber-200'
                      : 'text-red-800 dark:text-red-200'
                }`}>
                  {diferencia >= 0 ? '+' : ''}{formatCurrency(diferencia)}
                </p>
                <div className="flex items-center mt-1">
                  {balanceado ? (
                    <>
                      <CheckCircle className="h-3 w-3 text-emerald-600 dark:text-emerald-400 mr-1" />
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Balanceado</p>
                    </>
                  ) : diferencia > 0 ? (
                    <>
                      <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400 mr-1" />
                      <p className="text-xs text-amber-600 dark:text-amber-400">Sobrefacturado</p>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-3 w-3 text-red-600 dark:text-red-400 mr-1" />
                      <p className="text-xs text-red-600 dark:text-red-400">Falta facturar</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Facturas Emitidas - Resumen por tipo */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('invoices')}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="mr-3 h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Facturas Emitidas</p>
                    <p className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(salesData.invoiceSales)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{periodInvoices.length}</span>
                  {expandedSections.invoices ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>
              
              {expandedSections.invoices && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-600">
                  {/* Resumen por tipo de venta */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded text-center">
                      <p className="text-xs text-purple-700 dark:text-purple-300">Producciones</p>
                      <p className="text-sm font-bold text-purple-800 dark:text-purple-200">{formatCurrency(totalsBySaleType.production)}</p>
                      <p className="text-[10px] text-purple-600">{invoicesBySaleType.production.length} fact.</p>
                    </div>
                    <div className="bg-pink-50 dark:bg-pink-900/20 p-2 rounded text-center">
                      <p className="text-xs text-pink-700 dark:text-pink-300">Muestras</p>
                      <p className="text-sm font-bold text-pink-800 dark:text-pink-200">{formatCurrency(totalsBySaleType.sample)}</p>
                      <p className="text-[10px] text-pink-600">{invoicesBySaleType.sample.length} fact.</p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded text-center">
                      <p className="text-xs text-orange-700 dark:text-orange-300">Express</p>
                      <p className="text-sm font-bold text-orange-800 dark:text-orange-200">{formatCurrency(totalsBySaleType.express)}</p>
                      <p className="text-[10px] text-orange-600">{invoicesBySaleType.express.length} fact.</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded text-center">
                      <p className="text-xs text-amber-700 dark:text-amber-300">M. Express</p>
                      <p className="text-sm font-bold text-amber-800 dark:text-amber-200">{formatCurrency(totalsBySaleType.express_sample)}</p>
                      <p className="text-[10px] text-amber-600">{invoicesBySaleType.express_sample.length} fact.</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900/20 p-2 rounded text-center">
                      <p className="text-xs text-slate-700 dark:text-slate-300">Otros</p>
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200">{formatCurrency(totalsBySaleType.other)}</p>
                      <p className="text-[10px] text-slate-600">{invoicesBySaleType.other.length} fact.</p>
                    </div>
                  </div>
                  {invoicesBySaleType.sin_tipo.length > 0 && (
                    <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-700 rounded text-center">
                      <p className="text-xs text-gray-600 dark:text-gray-400">Sin tipo asignado: {formatCurrency(totalsBySaleType.sin_tipo)} ({invoicesBySaleType.sin_tipo.length} fact.)</p>
                    </div>
                  )}

                  {/* Lista de facturas */}
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {periodInvoices.length > 0 ? (
                      periodInvoices
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map((invoice) => {
                          const isUSD = invoice.currency === 'USD';
                          const originalAmount = invoice.subtotal || invoice.total || 0;
                          const convertedAmount = isUSD && invoice.exchange_rate ? originalAmount * invoice.exchange_rate : originalAmount;
                          const saleTypeLabels: Record<string, {label: string, color: string}> = {
                            production: { label: 'Prod', color: 'bg-purple-100 text-purple-700' },
                            sample: { label: 'Muestra', color: 'bg-pink-100 text-pink-700' },
                            express: { label: 'Express', color: 'bg-orange-100 text-orange-700' },
                            express_sample: { label: 'M.Exp', color: 'bg-amber-100 text-amber-700' },
                            other: { label: 'Otros', color: 'bg-slate-100 text-slate-700' },
                          };
                          const saleTypeInfo = invoice.sale_type ? saleTypeLabels[invoice.sale_type] : null;
                          
                          return (
                            <div key={invoice.id} className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900 dark:text-white">Factura {invoice.number}</p>
                                  {saleTypeInfo && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${saleTypeInfo.color}`}>
                                      {saleTypeInfo.label}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {getClientName(invoice.entity_id)} • {formatArgentinaDate(invoice.date)}
                                </p>
                                {invoice.salesperson && (
                                  <p className="text-xs text-gray-400 dark:text-gray-500">Vendedor: {invoice.salesperson}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(convertedAmount)}</p>
                                {isUSD && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400">USD {originalAmount.toFixed(2)}</p>
                                )}
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No hay facturas en este período</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Separador visual */}
            <div className="flex items-center gap-3">
              <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pedidos cargados para facturar</span>
              <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
            </div>

            {/* Producciones */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('productions')}
                className="w-full p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <Package className="mr-2 h-4 w-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Producciones ({periodProductions.length})</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(salesData.productionSales)}</span>
                  {expandedSections.productions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              </button>
              
              {expandedSections.productions && periodProductions.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-600 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {periodProductions.map((prod) => (
                      <div key={prod.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{prod.name || `Producción #${prod.id?.slice(0,8)}`}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{getClientName(prod.client_id)} • {formatArgentinaDate(prod.created_at)}</p>
                        </div>
                        <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(prod.production_value || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Muestras Pro */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('samples')}
                className="w-full p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <FlaskConical className="mr-2 h-4 w-4 text-pink-600 dark:text-pink-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Muestras Pro ({periodSamples.length})</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">{formatCurrency(salesData.sampleSales)}</span>
                  {expandedSections.samples ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              </button>
              
              {expandedSections.samples && periodSamples.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-600 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {periodSamples.map((sample) => (
                      <div key={sample.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{sample.name || `Muestra #${sample.id?.slice(0,8)}`}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{getClientName(sample.client_id)} • {formatArgentinaDate(sample.created_at)}</p>
                        </div>
                        <p className="text-sm font-semibold text-pink-600 dark:text-pink-400">{formatCurrency(sample.sample_value || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Express */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('express')}
                className="w-full p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <Zap className="mr-2 h-4 w-4 text-orange-600 dark:text-orange-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Express ({periodExpress.length})</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{formatCurrency(salesData.expressSales)}</span>
                  {expandedSections.express ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              </button>
              
              {expandedSections.express && periodExpress.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-600 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {periodExpress.map((order) => (
                      <div key={order.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Express #{order.id?.slice(0,8)}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {getClientName(order.client_id)} • {formatArgentinaDate(order.date || order.created_at)}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">{formatCurrency(order.express_value || order.total || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Muestras Express */}
            <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => toggleSection('expressSamples')}
                className="w-full p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center">
                  <FlaskConical className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Muestras Express ({periodExpressSamples.length})</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(salesData.expressSampleSales)}</span>
                  {expandedSections.expressSamples ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </div>
              </button>
              
              {expandedSections.expressSamples && periodExpressSamples.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-600 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {periodExpressSamples.map((sample) => (
                      <div key={sample.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Muestra Express #{sample.id?.slice(0,8)}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {getClientName(sample.client_id)} • {formatArgentinaDate(sample.date || sample.created_at)}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(sample.sample_value || sample.total || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => onClose()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Cerrar
            </button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default SalesDetailModal;
